import { Fragment } from "react";

export default function DaveHash({
    text,
    y_vowel = false,
    sylladex_size = 10,
    hash_fn = basic_hash,
}: {
    text: string;
    y_vowel?: boolean;
    sylladex_size?: number;
    hash_fn?: HashFn;
}): JSX.Element {
    let letters = text.toUpperCase().split("");
    let math_result = (
        <span> {math({ letters, y_vowel, sylladex_size, hash_fn })}</span>
    );
    // children.push();
    let letters__ = (
        <div style={{ display: "block" }} className="davehash">
            <Word letters={letters} y_vowel={y_vowel} hash_fn={hash_fn} />
            {math_result}
        </div>
    );

    return letters__;
}

function Word({
    letters,
    y_vowel,
    hash_fn,
}: {
    letters: string[];
    y_vowel: boolean;
    hash_fn: HashFn;
}): JSX.Element {
    let children = letters.map((letter, i) => (
        <Fragment key={i}>{Letter({ letter, y_vowel, hash_fn })}</Fragment>
    ));
    return <Fragment children={children}></Fragment>;
}

function math({
    letters,
    y_vowel,
    sylladex_size,
    hash_fn,
}: {
    letters: string[];
    y_vowel: boolean;
    sylladex_size: number;
    hash_fn: HashFn;
}): string {
    let nums: number[] = letters
        .filter(is_letter)
        .map(l => hash_fn(l, y_vowel).value);
    let sum = nums.reduce((a, b) => a + b, 0);

    return `(${
        nums.length > 0 ? nums.join("+") : 0
    } = ${sum}%${sylladex_size} = ${sum % sylladex_size})`;
}

type HashResult = { color: Color; value: number };
// prettier-ignore
type AsciiLetter =
    | "A" | "B" | "C" | "D" | "E"
    | "F" | "G" | "H" | "I" | "J"
    | "K" | "L" | "M" | "N" | "O"
    | "P" | "Q" | "R" | "S" | "T"
    | "U" | "V" | "W" | "X" | "Y"
    | "Z";
export type HashFn = (_: AsciiLetter, y_vowel: boolean) => HashResult;
type Color = string;
type HashLetterAssoc = [AsciiLetter, Color, number];

type HashInfo = { fn: HashFn; bla: HashLetterAssoc[] };

function Letter({
    letter,
    y_vowel,
    hash_fn,
}: {
    letter: string;
    y_vowel: boolean;
    hash_fn: HashFn;
}): JSX.Element {
    return is_letter(letter) ? (
        <span style={{ color: hash_fn(letter, y_vowel).color }}>{letter}</span>
    ) : (
        <span className="space">{letter}</span>
    );
}

const basic_hash: HashFn = (l, y_vowel) =>
    is_vowel(l, y_vowel)
        ? { color: "red", value: 1 }
        : { color: "blue", value: 2 };

function is_vowel(letter: AsciiLetter, y_vowel: boolean): boolean {
    let vowels = "aeiou".split("");
    if (y_vowel) {
        vowels.push("y");
    }
    return !vowels.every(v => v != letter.toLowerCase());
}

function is_letter(letter: string): letter is AsciiLetter {
    let letters = "QWERTYUIOPASDFGHJKLZXCVBNM".split("");
    return !letters.every(v => v != letter);
}

const reversed_hash: HashFn = (l, y_vowel) =>
    !is_vowel(l, y_vowel)
        ? { color: "red", value: 1 }
        : { color: "blue", value: 2 };

export function ShowHashDesc({ bla }: { bla: HashLetterAssoc[] }): JSX.Element {
    return (
        <div
            className="hash-desc"
            children={bla.map((hla, i) => (
                <Fragment key={i}>{ShowHashLetterAssoc({ hla })}</Fragment>
            ))}
        ></div>
    );
}

function ShowHashLetterAssoc({ hla }: { hla: HashLetterAssoc }): JSX.Element {
    return (
        <p className="hash-letter-assoc">
            <span style={{ color: hla[1] }}>{hla[0].toUpperCase()}</span>=
            {hla[2]};<span style={{ color: "#00000000" }}> </span>
        </p>
    );
}

const scrabble_hash: HashFn = (l, _) => {
    // prettier-ignore
    let dict = {
        A: 1, B: 3,  C: 3, D: 2, E: 1,
        F: 4, G: 2,  H: 4, I: 1, J: 8,
        K: 5, L: 1,  M: 3, N: 1, O: 1,
        P: 3, Q: 10, R: 1, S: 1, T: 1,
        U: 1, V: 4,  W: 4, X: 8, Y: 4,
        Z: 10,
    };
    return { color: "#05d000", value: dict[l] };
};

export const hashes: HashInfo[] = [
    {
        fn: basic_hash,
        bla: [
            ["C", "blue", 2],
            ["V", "red", 1],
        ],
    },
    {
        fn: reversed_hash,
        bla: [
            ["C", "red", 1],
            ["V", "blue", 2],
        ],
    },
    {
        fn: scrabble_hash,
        bla: "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
            .split("")
            .map(l => [
                l as AsciiLetter,
                "#05d000",
                scrabble_hash(l as AsciiLetter, false).value,
            ]),
    },
];
